import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { ES, GB } from "country-flag-icons/react/3x2";
import { FaTimes, FaExchangeAlt, FaCheckDouble } from "react-icons/fa";
import { useDebounce } from "@uidotdev/usehooks";
import { Confetti } from "@/ui";
import {
  callWithAuth,
  postTranslate,
  postDictionaryBulkAdd,
} from "@/api/Api";
import { withRetry } from "@/lib/utils";
import { BulkAddItem } from "@shared_types";

type Direction = "es-en" | "en-es";
type Status = "learning" | "known";

interface QueuedWord {
  id: string;
  source: string; // input text
  translation: string;
  spanish: string; // resolved Spanish (text for bulk-add)
  english: string; // resolved English (translation for bulk-add)
  status: Status;
}

interface TranslateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWordsAdded?: (counts: { known: number; learning: number }) => void;
}

const TRANSLATE_DEBOUNCE_MS = 350;
const CHIP_LEARNING_BG = "var(--tint-blue-soft)";
const CHIP_KNOWN_BG = "rgba(22,163,74,0.1)";

const FlagEmoji: React.FC<{ lang: "es" | "us" }> = ({ lang }) =>
  lang === "es" ? (
    <ES title="Spanish" className="w-5 h-[14px] inline-block" />
  ) : (
    <GB title="English" className="w-5 h-[14px] inline-block" />
  );

const TranslateModal: React.FC<TranslateModalProps> = ({
  isOpen,
  onClose,
  onWordsAdded,
}) => {
  // Default English→Spanish: "add your own word" is an English speaker typing an
  // English word to get its Spanish (and save it). iOS defaults es-en, but that
  // made the web feel broken — opening on "Type in Spanish" and typing English
  // returns nothing. Swap still flips to Spanish→English.
  const [direction, setDirection] = useState<Direction>("en-es");
  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  // Whether `translatedText` is the visible error string rather than a real
  // translation — tracked separately so the failure message can't be added to
  // the dictionary (it's still shown in the target card for parity with iOS).
  const [translateFailed, setTranslateFailed] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [queue, setQueue] = useState<QueuedWord[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successCount, setSuccessCount] = useState(0);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pendingCountsRef = useRef<{ known: number; learning: number } | null>(
    null
  );
  const translateSeqRef = useRef(0);
  // Guards against the celebration being dismissed twice — once by the
  // Confetti onDone callback and once by the self-owned safety timer below.
  const celebrationDoneRef = useRef(false);
  const debouncedInput = useDebounce(inputText.trim(), TRANSLATE_DEBOUNCE_MS);

  const {
    isOpen: isDiscardOpen,
    onOpen: openDiscard,
    onClose: closeDiscard,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const sourceLang: "es" | "us" = direction === "es-en" ? "es" : "us";
  const targetLang: "es" | "us" = direction === "es-en" ? "us" : "es";
  const sourceLabel = direction === "es-en" ? "Spanish" : "English";
  const targetLabel = direction === "es-en" ? "English" : "Spanish";
  const sourcePlaceholder =
    direction === "es-en" ? "Type in Spanish..." : "Type in English...";

  // Reset everything when the modal opens.
  useEffect(() => {
    if (isOpen) {
      setDirection("en-es");
      setInputText("");
      setTranslatedText("");
      setTranslateFailed(false);
      setQueue([]);
      setShowSuccess(false);
      setSuccessCount(0);
      celebrationDoneRef.current = false;
      const t = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Debounced auto-translate.
  useEffect(() => {
    if (!debouncedInput) {
      setTranslatedText("");
      setTranslateFailed(false);
      setIsTranslating(false);
      return;
    }
    const seq = ++translateSeqRef.current;
    setIsTranslating(true);
    // The translate endpoint (LLM-backed) returns intermittent 500s; retry a
    // couple times so a transient failure doesn't surface as "Translation failed".
    withRetry(
      () => callWithAuth(postTranslate, { text: debouncedInput, direction }),
      2,
      500
    )
      .then((res) => {
        if (seq !== translateSeqRef.current) return; // stale
        setTranslatedText(res?.translation ?? "");
        setTranslateFailed(false);
      })
      .catch((err) => {
        if (seq !== translateSeqRef.current) return;
        console.error("translate error", err);
        // iOS sets the visible string "Translation failed" on error
        // (TranslateModal.tsx:354) rather than clearing the field.
        setTranslatedText("Translation failed");
        setTranslateFailed(true);
      })
      .finally(() => {
        if (seq === translateSeqRef.current) setIsTranslating(false);
      });
  }, [debouncedInput, direction]);

  const handleSwap = () => {
    // Match iOS handleSwapLanguages (TranslateModal.tsx:362-369): flip the
    // direction and KEEP the typed text, then re-translate it the other way.
    // Clearing the input here was the bug — after typing a word the user could
    // not flip to the opposite direction and see it translate; the text was
    // thrown away. The [debouncedInput, direction] effect re-fires on the new
    // direction with the retained text, so English→Spanish (and vice-versa)
    // now resolves immediately on swap.
    // Invalidate any in-flight translate request: bumping the seq here means a
    // pre-swap response that resolves before the new direction's effect runs is
    // treated as stale, so an old-direction translation can't be left addable
    // for the new direction.
    translateSeqRef.current += 1;
    setDirection((d) => (d === "es-en" ? "en-es" : "es-en"));
    setTranslatedText("");
    setTranslateFailed(false);
  };

  // Only addable when the shown translation actually corresponds to the current
  // input: after editing the source, the previous translation lingers until the
  // debounce/refetch lands, so gate on the trimmed input matching the debounced
  // value and on no in-flight translation (else the new source could be queued
  // with the stale translation).
  const trimmedInput = inputText.trim();
  const canAdd =
    !!translatedText &&
    !translateFailed &&
    !isTranslating &&
    !!trimmedInput &&
    trimmedInput === debouncedInput;

  const handleAdd = (status: Status) => {
    if (!canAdd) return;
    const src = inputText.trim();
    // Resolve which side is Spanish / English for bulk-add.
    const spanish = direction === "es-en" ? src : translatedText;
    const english = direction === "es-en" ? translatedText : src;
    setQueue((q) => [
      ...q,
      {
        id: `${Date.now()}-${Math.random()}`,
        source: src,
        translation: translatedText,
        spanish,
        english,
        status,
      },
    ]);
    setInputText("");
    setTranslatedText("");
    setTranslateFailed(false);
    inputRef.current?.focus();
  };

  const handleRemoveFromQueue = (id: string) => {
    setQueue((q) => q.filter((w) => w.id !== id));
  };

  const handleDone = async () => {
    if (queue.length === 0) return;
    // iOS handleDone (TranslateModal.tsx:405-432) calls Keyboard.dismiss()
    // first. The web analog is blurring the textarea so the celebration isn't
    // fighting an open soft-keyboard / focused field.
    inputRef.current?.blur();
    const items: BulkAddItem[] = queue.map((w) => ({
      text: w.spanish,
      translation: w.english,
      status: w.status,
    }));
    const known = queue.filter((w) => w.status === "known").length;
    const learning = queue.filter((w) => w.status === "learning").length;
    // Match iOS: show the celebration immediately, then fire the request. The
    // count mutation (pendingWordsAdded) is only set AFTER the bulk-add resolves
    // (TranslateModal.tsx:422-428), so a failed save doesn't inflate counts.
    celebrationDoneRef.current = false;
    setSuccessCount(queue.length);
    setShowSuccess(true);
    try {
      await callWithAuth(postDictionaryBulkAdd, { items });
      pendingCountsRef.current = { known, learning };
      // Clear the queue only AFTER the save succeeds — otherwise a failed
      // bulk-add would drop the celebration's words with no way to retry.
      setQueue([]);
    } catch (err) {
      console.error("bulk add error", err);
      // Save failed: keep the queued words and drop the celebration so the user
      // can retry instead of losing their work behind a false success screen.
      setShowSuccess(false);
      celebrationDoneRef.current = true;
    }
  };

  const handleCelebrationFinished = useCallback(() => {
    // Idempotent: the Confetti onDone callback AND the safety timer below both
    // call this, but the modal must only close once.
    if (celebrationDoneRef.current) return;
    celebrationDoneRef.current = true;
    setShowSuccess(false);
    const pending = pendingCountsRef.current;
    pendingCountsRef.current = null;
    onClose();
    if (pending) onWordsAdded?.(pending);
  }, [onClose, onWordsAdded]);

  // BUG FIX: guarantee the celebration always ends in a closeable state.
  // Previously dismissal hinged ENTIRELY on Confetti's onDone firing — and
  // because handleRequestClose returns early while showSuccess is true, the X
  // button and overlay are inert during the celebration. If Confetti's onDone
  // never fired (e.g. its canvas ref was null on first paint, hitting the
  // `if (!canvas) return;` early-return BEFORE the done-timer was scheduled, or
  // a worker/reduced-motion failure), the user was trapped on the "Added N
  // words!" overlay with no way out. iOS does NOT couple dismissal to particle
  // rendering: CelebrationOverlay owns its own setTimeout(2200) that calls
  // onFinished regardless (TranslateModal.tsx:188-194). Mirror that here so the
  // modal self-dismisses even if the visual confetti misbehaves.
  const CELEBRATION_MS = 2200; // matches iOS CelebrationOverlay timeout
  useEffect(() => {
    if (!showSuccess) return;
    const t = setTimeout(handleCelebrationFinished, CELEBRATION_MS);
    return () => clearTimeout(t);
  }, [showSuccess, handleCelebrationFinished]);

  const handleRequestClose = () => {
    if (showSuccess) return;
    if (queue.length > 0) {
      openDiscard();
      return;
    }
    onClose();
  };

  const handleDiscard = () => {
    closeDiscard();
    setQueue([]);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleRequestClose}
        isCentered
        scrollBehavior="inside"
        size="md"
      >
        <ModalOverlay />
        <ModalContent
          bg="var(--color-grey-lightest)"
          borderRadius="2xl"
          mx={3}
          maxW="480px"
        >
          <ModalBody
            p={0}
            position="relative"
            onMouseDown={(e) => {
              // iOS wraps the sheet in <Pressable onPress={Keyboard.dismiss}>
              // (TranslateModal.tsx:493) so tapping outside the field dismisses
              // the keyboard. Web analog: a mousedown anywhere that isn't the
              // textarea blurs it. Using mousedown (not click) means the blur
              // lands before any button's click handler, so action buttons and
              // the close button still work normally.
              if (e.target !== inputRef.current) {
                inputRef.current?.blur();
              }
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <div className="text-2xl font-bold text-[var(--color-text-primary)]">
                Translate &amp; Add
              </div>
              <button
                aria-label="Close"
                onClick={handleRequestClose}
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#E5E7EB" }}
              >
                <FaTimes className="w-[18px] h-[18px] text-[var(--color-grey)]" />
              </button>
            </div>

            {/* Language row */}
            <div className="flex items-center justify-center gap-3 px-5 pb-4">
              <div className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-xl text-[var(--color-blue)] text-sm font-semibold">
                <FlagEmoji lang={sourceLang} /> {sourceLabel}
              </div>
              <button
                aria-label="Swap languages"
                onClick={handleSwap}
                className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0"
              >
                <FaExchangeAlt className="w-[18px] h-[18px] text-[var(--color-blue)]" />
              </button>
              <div className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-xl text-[var(--color-blue)] text-sm font-semibold">
                <FlagEmoji lang={targetLang} /> {targetLabel}
              </div>
            </div>

            {/* Source card */}
            <div className="bg-white mx-5 mb-3 rounded-2xl p-4 min-h-[100px]">
              <div className="text-[11px] font-medium text-[var(--color-grey-other)] tracking-wider mb-2">
                {sourceLabel.toUpperCase()}
              </div>
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  // iOS: returnKeyType="done" + blurOnSubmit + onSubmitEditing
                  // -> Keyboard.dismiss() (TranslateModal.tsx:552-554). On web,
                  // Enter (without Shift) blurs the field instead of inserting a
                  // newline, so the user can dismiss the on-screen keyboard.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    inputRef.current?.blur();
                  }
                }}
                placeholder={sourcePlaceholder}
                maxLength={200}
                rows={2}
                className="w-full resize-none outline-none text-xl font-medium text-[var(--color-text-primary)] bg-transparent placeholder:text-[var(--color-grey-other)]"
              />
            </div>

            {/* Target card */}
            <div className="bg-white mx-5 mb-3 rounded-2xl p-4 min-h-[100px]">
              <div className="text-[11px] font-medium text-[var(--color-grey-other)] tracking-wider mb-2">
                {targetLabel.toUpperCase()}
              </div>
              {isTranslating ? (
                <Spinner size="sm" color="var(--color-blue)" mt={3} />
              ) : translatedText ? (
                <div className="text-lg font-medium text-[var(--color-text-primary)] mt-1">
                  {translatedText}
                </div>
              ) : (
                <div className="text-lg text-[var(--color-grey-other)] mt-1">
                  Translation will appear as you type
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2.5 px-5">
              <button
                onClick={() => handleAdd("learning")}
                disabled={!canAdd}
                className="flex-1 py-3.5 rounded-2xl text-white text-sm font-semibold disabled:opacity-40"
                style={{ backgroundColor: "var(--color-blue)" }}
              >
                I'm learning this
              </button>
              <button
                onClick={() => handleAdd("known")}
                disabled={!canAdd}
                className="flex-1 py-3.5 rounded-2xl text-white text-sm font-semibold disabled:opacity-40"
                style={{ backgroundColor: "var(--color-green)" }}
              >
                I already know this
              </button>
            </div>

            {/* Queue */}
            {queue.length > 0 && (
              <div className="mt-5 pl-5 pb-5">
                <button
                  onClick={handleDone}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white text-base font-bold mb-3"
                  style={{ backgroundColor: "var(--color-blue)", marginRight: 20, width: "calc(100% - 20px)" }}
                >
                  <FaCheckDouble className="w-4 h-4" />
                  Done — Add {queue.length}{" "}
                  {queue.length === 1 ? "word" : "words"}
                </button>
                <div className="text-[11px] font-semibold text-[var(--color-grey-other)] tracking-wider mb-2.5">
                  QUEUED ({queue.length})
                </div>
                <div className="flex gap-2 overflow-x-auto pr-5">
                  {queue.map((w) => (
                    <div
                      key={w.id}
                      className="flex items-center gap-1.5 py-2 pl-3 pr-1.5 rounded-full flex-shrink-0 max-w-[150px]"
                      style={{
                        backgroundColor:
                          w.status === "known"
                            ? CHIP_KNOWN_BG
                            : CHIP_LEARNING_BG,
                      }}
                    >
                      <span className="text-[11px] font-semibold text-[var(--color-text-primary)] truncate">
                        {w.source}
                      </span>
                      <button
                        aria-label="Remove"
                        onClick={() => handleRemoveFromQueue(w.id)}
                        className="w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
                      >
                        <FaTimes className="w-3 h-3 text-[var(--color-grey)]" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Celebration overlay */}
            {showSuccess && (
              <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center rounded-2xl z-10 px-6 text-center">
                <Confetti active={showSuccess} onDone={handleCelebrationFinished} />
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "var(--color-green)" }}>
                  <FaCheckDouble className="w-9 h-9 text-white" />
                </div>
                <div className="text-2xl font-bold text-[var(--color-text-primary)]">
                  Added {successCount} {successCount === 1 ? "word" : "words"}! 🎉
                </div>
                <div className="text-base text-[var(--color-grey-other)] mt-1">
                  Nice one! Keep building your vocabulary
                </div>
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Discard guard */}
      <AlertDialog
        isOpen={isDiscardOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDiscard}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={3}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              You have {queue.length} unsaved{" "}
              {queue.length === 1 ? "word" : "words"}
            </AlertDialogHeader>
            <AlertDialogBody>
              Add them to your dictionary before closing?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button colorScheme="red" variant="ghost" onClick={handleDiscard}>
                Discard
              </Button>
              <Button ref={cancelRef} onClick={closeDiscard} ml={3}>
                Cancel
              </Button>
              <Button
                ml={3}
                color="white"
                bg="var(--color-blue)"
                _hover={{ bg: "var(--color-blue)" }}
                onClick={() => {
                  closeDiscard();
                  handleDone();
                }}
              >
                Add {queue.length}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default TranslateModal;
